import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../redux-store";
import { CusTomTimelineRow } from "../../Pages/Timetool/custom-timeline-row";
import { CustomTimelineAction } from "../../Pages/Timetool/custom-timeline-action";
import { DbFileInfos } from "../../Core/Api/Types/db-file-infos";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { v4 as uuidv4 } from "uuid";

export interface ReduxStateTimetool {
  loadedSequence: DbAssetObjectInfos | null;
  sequenceName: string;
  src: string;
  showSequence: boolean;
  showTimeline: boolean;
  showLibrary: boolean;
  currentFrame: number;
  timelineActions: CusTomTimelineRow[];
  orderedActionsList: CustomTimelineAction[];
  fps: number;
  previewFps: number;
  selectedAsset: any;
  selectedAssetFile: DbFileInfos | undefined;
  sequenceIsPlaying: boolean;
  sequenceTime: number;
  previewTime: number;
  previewIsPlaying: boolean;
  sequenceDuration: number;
  inFrame: number | null;
  outFrame: number | null;
  previewDuration: number;
  previewSrc: string;
  context: "preview" | "sequence" | "clip";
  loadedAssets: any[];
  linkedToPreviewActionId: string;
  isFetching: boolean;
  playAfterFetching: "preview" | "sequence" | undefined;
  editMode: "default" | "cut";
  resetInAndOutFrame: boolean;
  isLoading: boolean;
  playerError: string | null;
  zoomState: { scale: number; width: number };
  autoScroll: boolean;
  cacheSequenceTime: number;
  audioPlayMode: "mono" | "stereo";
  audioState: { trackIndex: number; selectedChannels: "left" | "right" | "both" | "none" };
  fetchingVideoFileData: boolean;
  handleAudio: boolean;
}

const initialState: ReduxStateTimetool = {
  loadedSequence: null,
  sequenceName: "untitled",
  src: "",
  showSequence: true,
  showTimeline: true,
  showLibrary: true,
  currentFrame: 0,
  timelineActions: [{ id: "0", actions: [] }],
  orderedActionsList: [],
  fps: 24,
  previewFps: 24,
  selectedAsset: null,
  selectedAssetFile: undefined,
  sequenceIsPlaying: false,
  previewTime: 0,
  previewIsPlaying: false,
  sequenceTime: 0,
  sequenceDuration: 0,
  inFrame: null,
  outFrame: null,
  previewDuration: 1,
  previewSrc: "",
  context: "preview",
  loadedAssets: [],
  linkedToPreviewActionId: "",
  isFetching: false,
  playAfterFetching: undefined,
  editMode: "default",
  resetInAndOutFrame: false,
  isLoading: false,
  playerError: null,
  zoomState: { scale: 1800, width: 294 },
  autoScroll: true,
  cacheSequenceTime: 0,
  audioPlayMode: "mono",
  audioState: { selectedChannels: "both", trackIndex: 0 },
  fetchingVideoFileData: false,
  handleAudio: false,
};

export const timetoolSlice = createSlice({
  name: "timetool",
  initialState,
  reducers: {
    InitState: (state) => {
      state = initialState;
    },
    CacheAfterError: (state, action) => {
      state.playerError = action.payload;
      if (state.cacheSequenceTime === 0) state.cacheSequenceTime = state.sequenceTime;
      state.sequenceTime = 0;
      state.previewTime = 0;
    },
    ResetAfterError: (state, action) => {
      state.playerError = null;
      if (state.cacheSequenceTime !== 0) state.sequenceTime = state.cacheSequenceTime;
      state.cacheSequenceTime = 0;
    },
    SetIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    SetLoadedSequence: (state, action: PayloadAction<DbAssetObjectInfos>) => {
      state.loadedSequence = action.payload;
      state.sequenceName = action.payload.name;
    },
    SetSequenceName: (state, action: PayloadAction<string>) => {
      state.sequenceName = action.payload;
    },
    SetFps: (state, action: PayloadAction<number>) => {
      const previousFps = state.fps;
      state.fps = action.payload;
      const newTimelineActions = state.timelineActions.map((x) => {
        return {
          ...x,
          actions: x.actions.map((a) => {
            return { ...a, data: { ...a.data, duration: (a.data.duration * previousFps) / action.payload } };
          }),
        };
      });
      const newCalculateTimeline = calculateNextTimelineState(
        [...state.timelineActions],
        newTimelineActions,
        action.payload,
        true
      );
      state.timelineActions = newCalculateTimeline;
      state.orderedActionsList = calculateOrderedActions(newCalculateTimeline, action.payload);
    },
    SetIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    SetLinkedToPreviewActionId: (state, action: PayloadAction<string>) => {
      state.linkedToPreviewActionId = action.payload;
    },
    TogglePreviewIsPlaying: (state) => {
      state.previewIsPlaying = !state.previewIsPlaying;
    },
    SetContext: (state, action: PayloadAction<"clip" | "preview" | "sequence">) => {
      state.context = action.payload;
    },
    SetPreviewSrc: (state, action: PayloadAction<string>) => {
      state.previewSrc = action.payload;
    },
    SetInFrame: (state, action: PayloadAction<number>) => {
      if (state.outFrame && state.outFrame <= action.payload) return;

      if (state.linkedToPreviewActionId !== "" && state.inFrame !== null) {
        let newTimelineActions = calculateNextTimelineState(
          state.timelineActions,
          state.timelineActions.map((x) => ({
            ...x,
            actions: x.actions.map((a) => {
              if (a.id === state.linkedToPreviewActionId) {
                return { ...a, start: a.start - (a.data.offSet - action.payload) / state.fps };
              } else return a;
            }),
          })),
          state.fps
        );

        let newInFrame = -1;
        newTimelineActions.forEach((x) =>
          x.actions.forEach((a) => {
            if (a.id === state.linkedToPreviewActionId) {
              newInFrame = a.data.offSet;
            }
          })
        );
        if (newInFrame > -1) {
          state.timelineActions = newTimelineActions;
          state.orderedActionsList = calculateOrderedActions(newTimelineActions, state.fps);
          state.inFrame = Math.round(newInFrame);
          state.previewTime = Math.round(newInFrame) / state.previewFps;
        }
        state.resetInAndOutFrame = !state.resetInAndOutFrame;
      } else {
        state.inFrame = action.payload;
        state.previewTime = Math.round(action.payload) / state.previewFps;
      }
    },
    SetOutFrame: (state, action: PayloadAction<number>) => {
      if ((state.inFrame !== null && state.inFrame >= action.payload) || action.payload > state.previewDuration) return;

      if (state.linkedToPreviewActionId !== "" && state.outFrame !== null) {
        let newTimelineActions = calculateNextTimelineState(
          state.timelineActions,
          state.timelineActions.map((x) => ({
            ...x,
            actions: x.actions.map((a) => {
              if (a.id === state.linkedToPreviewActionId) {
                const diff = action.payload - ((a.end - a.start) * state.fps + a.data.offSet);
                return {
                  ...a,
                  end: a.end + diff / state.fps,
                };
              } else return a;
            }),
          })),
          state.fps
        );

        let newOutFrame = -1;

        newTimelineActions.forEach((x) =>
          x.actions.forEach((a) => {
            if (a.id === state.linkedToPreviewActionId) {
              newOutFrame = (a.end - a.start) * state.fps + a.data.offSet;
            }
          })
        );

        if (newOutFrame > -1) {
          state.timelineActions = newTimelineActions;
          state.orderedActionsList = calculateOrderedActions(newTimelineActions, state.fps);
          state.outFrame = Math.round(newOutFrame);
          state.previewTime = Math.round(newOutFrame) / state.previewFps;
        }
        state.resetInAndOutFrame = !state.resetInAndOutFrame;
      } else {
        state.outFrame = action.payload;
        state.previewTime = Math.round(action.payload) / state.previewFps;
      }
    },
    SetInAndOutFrame: (state, action: PayloadAction<{ inFrame: number; outFrame: number }>) => {
      if (action.payload.inFrame >= action.payload.outFrame) return;

      state.inFrame = action.payload.inFrame;
      state.outFrame = action.payload.outFrame;
      state.previewTime = action.payload.inFrame / state.previewFps;
    },
    SetPreviewDuration: (state, action: PayloadAction<number>) => {
      state.previewDuration = action.payload;
      state.inFrame = 0;
      action.payload > 2 ? (state.outFrame = 1) : (state.outFrame = 0);
    },
    SetPreviewTime: (state, action: PayloadAction<number>) => {
      state.previewTime = action.payload;
    },
    SetPreviewIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.previewIsPlaying = action.payload;
    },
    SetSequenceTime: (state, action: PayloadAction<number>) => {
      state.sequenceTime = action.payload;
    },
    SetSequenceIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.sequenceIsPlaying = action.payload;
      if (action.payload) state.showSequence = true;
    },
    SetSrc: (state, action: PayloadAction<string>) => {
      state.src = action.payload;
    },
    SetShowSequence: (state, action: PayloadAction<boolean>) => {
      state.showSequence = action.payload;
    },
    ToggleSequence: (state) => {
      state.showSequence = !state.showSequence;
    },
    ToggleTimeline: (state) => {
      state.showTimeline = !state.showTimeline;
    },
    ToggleLibrary: (state) => {
      state.showLibrary = !state.showLibrary;
    },
    SetCurrentFrame: (state, action: PayloadAction<number>) => {
      state.currentFrame = action.payload;
    },
    SetTimelineActions: (state, action: PayloadAction<CusTomTimelineRow[]>) => {
      state.timelineActions = action.payload;
      const orderedActions = calculateOrderedActions(action.payload, state.fps);
      state.orderedActionsList = orderedActions;
      if (orderedActions.length > 0)
        state.sequenceDuration = Math.round(orderedActions[orderedActions.length - 1].end * state.fps);
      else state.sequenceDuration = 0;

      if (state.linkedToPreviewActionId !== "") {
        action.payload.forEach((x) => {
          x.actions.forEach((a) => {
            if (a.id === state.linkedToPreviewActionId) {
              state.inFrame = a.data.offSet;
              state.outFrame = (a.end - a.start) * state.fps + a.data.offSet;
            }
          });
        });
      }

      state.showSequence = true;
      state.showTimeline = true;
    },
    SetSelectedAsset: (state, action: PayloadAction<any>) => {
      state.selectedAsset = action.payload;
    },
    SetSelectedAssetFile: (state, action: PayloadAction<DbFileInfos>) => {
      state.selectedAssetFile = action.payload;
    },
    ChangeActionRow: (state, action: PayloadAction<{ actionId: string; direction: "down" | "up" }>) => {
      const { actionId, direction } = action.payload;

      let matchingRow = state.timelineActions.find((x) => x.actions.find((a) => a.id === actionId));

      if (matchingRow === undefined) return;

      let actionToMove = matchingRow.actions.find((x) => x.id === actionId);
      let matchingRowIndex = state.timelineActions.findIndex((x) => x.id === matchingRow?.id);

      if (actionToMove === undefined) return;

      if (direction === "down") {
        const nextFreeRowIndex = state.timelineActions.findIndex((x, i) => {
          if (i <= matchingRowIndex) return false;
          let free = true;
          for (let i = 0; i < x.actions.length; i++) {
            if (
              (x.actions[i].start > actionToMove!.start && x.actions[i].start < actionToMove!.end) ||
              (x.actions[i].end > actionToMove!.start && x.actions[i].end < actionToMove!.end) ||
              (x.actions[i].start < actionToMove!.start && x.actions[i].end > actionToMove!.end)
            ) {
              free = false;
            }
          }
          return free;
        });

        nextFreeRowIndex >= 0
          ? (state.timelineActions[nextFreeRowIndex] = {
              ...state.timelineActions[nextFreeRowIndex],
              actions: [actionToMove, ...state.timelineActions[nextFreeRowIndex].actions],
            })
          : state.timelineActions.push({ id: state.timelineActions.length.toString(), actions: [actionToMove] });

        state.timelineActions[matchingRowIndex] = {
          ...state.timelineActions[matchingRowIndex],
          actions: state.timelineActions[matchingRowIndex].actions.filter((x) => x.id !== actionId),
        };
      } else {
        const previousFreeRowIndex = state.timelineActions.findLastIndex((x, i) => {
          if (i >= matchingRowIndex) return false;
          let free = true;
          for (let i = 0; i < x.actions.length; i++) {
            if (
              (x.actions[i].start > actionToMove!.start && x.actions[i].start < actionToMove!.end) ||
              (x.actions[i].end > actionToMove!.start && x.actions[i].end < actionToMove!.end) ||
              (x.actions[i].start < actionToMove!.start && x.actions[i].end > actionToMove!.end)
            ) {
              free = false;
            }
          }
          return free;
        });
        if (previousFreeRowIndex >= 0) {
          state.timelineActions[previousFreeRowIndex] = {
            ...state.timelineActions[previousFreeRowIndex],
            actions: [actionToMove, ...state.timelineActions[previousFreeRowIndex].actions],
          };

          state.timelineActions[matchingRowIndex] = {
            ...state.timelineActions[matchingRowIndex],
            actions: state.timelineActions[matchingRowIndex].actions.filter((x) => x.id !== actionId),
          };
        }
      }
    },
    GenerateCalculatedSequence: (state, action: PayloadAction<boolean>) => {
      const filteredActions = action.payload
        ? state.orderedActionsList.filter((x) => x.data.src !== "").sort((a, b) => 0.5 - Math.random())
        : state.orderedActionsList.filter((x) => x.data.src !== "");
      const newTimelineActions: CustomTimelineAction[] = [];

      filteredActions.forEach((x, i) => {
        if (i === 0) {
          newTimelineActions.push({
            ...x,
            start: 0,
            end: Math.round((x.end - x.start) * 100) / 100,
          });
        } else {
          newTimelineActions.push({
            ...x,
            start: newTimelineActions[i - 1].end,
            end: Math.round((x.end - x.start + newTimelineActions[i - 1].end) * 100) / 100,
          });
        }
      });

      state.timelineActions = [{ id: "0", actions: newTimelineActions.filter((x) => x.start !== x.end) }];
      state.orderedActionsList = newTimelineActions.filter((x) => x.start !== x.end);
      state.sequenceDuration = newTimelineActions
        ? Math.round(newTimelineActions[newTimelineActions.length - 1].end * state.fps)
        : 0;
      state.linkedToPreviewActionId = "";
    },
    DeleteAction: (state, action: PayloadAction<string>) => {
      const newTimelineData = state.timelineActions.map((x) => ({
        ...x,
        actions: x.actions.filter((a) => a.id !== action.payload),
      }));
      state.timelineActions = newTimelineData;
      state.orderedActionsList = calculateOrderedActions(newTimelineData, state.fps);
      state.sequenceDuration =
        state.orderedActionsList.length > 0
          ? Math.round(state.orderedActionsList[state.orderedActionsList.length - 1].end * state.fps)
          : 0;
    },

    AddFirst: (state, action: PayloadAction<string>) => {
      const newTimelineActions = state.timelineActions.map((x) => {
        const foundAction = x.actions.find((a) => a.id === action.payload);
        if (foundAction) {
          return {
            ...x,
            actions: [
              { ...foundAction, start: 0, end: foundAction.end - foundAction.start },
              ...x.actions
                .filter((a) => a.id !== action.payload)
                .map((a) => {
                  if (a.start < foundAction.start) {
                    return {
                      ...a,
                      start: a.start + foundAction.end - foundAction.start,
                      end: a.end + foundAction.end - foundAction.start,
                    };
                  } else return a;
                }),
            ],
          };
        } else return x;
      });
      state.orderedActionsList = calculateOrderedActions(newTimelineActions, state.fps);
      state.timelineActions = newTimelineActions;
    },

    AddLast: (state, action: PayloadAction<string>) => {
      const newTimelineActions = state.timelineActions.map((x) => {
        const foundAction = x.actions.find((a) => a.id === action.payload);
        if (foundAction) {
          let rowEnd = foundAction.end;
          x.actions.forEach((a) => {
            if (a.end > rowEnd) rowEnd = a.end;
          });
          return {
            ...x,
            actions: [
              { ...foundAction, start: rowEnd - (foundAction.end - foundAction.start), end: rowEnd },
              ...x.actions
                .filter((a) => a.id !== action.payload)
                .map((a) => {
                  if (a.start > foundAction.start) {
                    return {
                      ...a,
                      start: a.start - (foundAction.end - foundAction.start),
                      end: a.end - (foundAction.end - foundAction.start),
                    };
                  } else return a;
                }),
            ],
          };
        } else return x;
      });
      state.orderedActionsList = calculateOrderedActions(newTimelineActions, state.fps);
      state.timelineActions = newTimelineActions;
    },
    ChangeEditMode: (state, action: PayloadAction<"default" | "cut">) => {
      state.editMode = action.payload;
    },

    CutAction: (state, action: PayloadAction<{ actionId: string; cut: number }>) => {
      const newTimelineState = state.timelineActions.map((x) => {
        const foundAction = x.actions.find((a) => a.id === action.payload.actionId);
        if (foundAction) {
          if (action.payload.cut >= foundAction.end || action.payload.cut <= foundAction.start) return x;
          const actionId = uuidv4();
          return {
            ...x,
            actions: [
              { ...foundAction, start: foundAction.start, end: action.payload.cut },
              {
                ...foundAction,
                start: action.payload.cut,
                end: foundAction.end,
                id: actionId,
                data: {
                  ...foundAction.data,
                  offSet: Math.round(
                    foundAction.data.offSet + (action.payload.cut - foundAction.start) * foundAction.data.fps
                  ),
                  actionId: actionId,
                },
              },
              ...x.actions.filter((a) => a.id !== action.payload.actionId),
            ],
          };
        } else return x;
      });

      if (state.linkedToPreviewActionId !== "") {
        newTimelineState.forEach((x) =>
          x.actions.forEach((a) => {
            if (a.id === state.linkedToPreviewActionId) {
              state.outFrame = (a.end - a.start) * state.fps + a.data.offSet;
            }
          })
        );
      }

      state.timelineActions = newTimelineState;
      state.orderedActionsList = calculateOrderedActions(newTimelineState, state.fps);
    },
    SetZoomState: (state, action: PayloadAction<{ scale: number; width: number }>) => {
      state.zoomState.scale = parseInt(action.payload.scale.toString());
      state.zoomState.width = parseInt(action.payload.width.toString());
    },
    CutAtCursorPos: (state) => {
      let actionIdToCut;
      state.timelineActions.forEach((x) => {
        if (!actionIdToCut)
          actionIdToCut = x.actions.find(
            (x) => x.start + 0.1 < state.sequenceTime && x.end - 0.1 > state.sequenceTime
          )?.id;
      });

      if (!actionIdToCut) return;

      const newTimelineState = state.timelineActions.map((x) => {
        const foundAction = x.actions.find((a) => a.id === actionIdToCut);
        if (foundAction) {
          if (state.sequenceTime >= foundAction.end || state.sequenceTime <= foundAction.start) return x;

          const actionId = uuidv4();
          return {
            ...x,
            actions: [
              { ...foundAction, start: foundAction.start, end: Math.round(state.sequenceTime * 100) / 100 },
              {
                ...foundAction,
                start: Math.round(state.sequenceTime * 100) / 100,
                end: foundAction.end,
                id: actionId,
                data: {
                  ...foundAction.data,
                  offSet:
                    foundAction.data.offSet +
                    Math.round((Math.round(state.sequenceTime * 100) / 100 - foundAction.start) * foundAction.data.fps),
                  actionId: actionId,
                },
              },
              ...x.actions.filter((a) => a.id !== actionIdToCut),
            ],
          };
        } else return x;
      });

      if (state.linkedToPreviewActionId !== "") {
        newTimelineState.forEach((x) =>
          x.actions.forEach((a) => {
            if (a.id === state.linkedToPreviewActionId) {
              state.outFrame = (a.end - a.start) * state.fps + a.data.offSet;
            }
          })
        );
      }

      state.timelineActions = newTimelineState;
      state.orderedActionsList = calculateOrderedActions(newTimelineState, state.fps);
    },

    SetLoadedAssets: (state, action: PayloadAction<any[]>) => {
      state.loadedAssets = action.payload;
    },

    AddLoadedAssets: (state, action: PayloadAction<any>) => {
      if (state.loadedAssets.find((x) => x.id === action.payload.id)) return;
      state.loadedAssets = [...state.loadedAssets, action.payload];
    },
    AddToSequence: (state, action: PayloadAction<{ asset: any; rowSrc: string }>) => {
      const { asset, rowSrc } = action.payload;
      const lastSequenceEnd =
        state.timelineActions.length > 0
          ? state.timelineActions.reduce((acc, x) => {
              let result = acc;
              if (x.actions && x.actions.length > 0) {
                for (let i = 0; i < x.actions.length; i++) {
                  if (x.actions[i].end > result) result = x.actions[i].end;
                }
              }
              return result;
            }, 0)
          : 0;

      if (lastSequenceEnd === 0 && asset.fps) {
        state.fps = asset.fps;
      }
      const actionId = uuidv4();

      const actionEnd = asset.frameOut
        ? lastSequenceEnd + (asset.frameOut - asset.frameIn) / state.fps
        : lastSequenceEnd + (asset.duration * state.fps) / asset.fps;

      let newAction: CustomTimelineAction = {
        data: {
          src: rowSrc,
          offSet: asset.frameIn || 0,
          name: asset.name || "",
          id: asset.assetId,
          type: asset.type,
          classification: asset.classification,
          duration: asset.videoRefDuration
            ? (asset.videoRefDuration * state.fps) / asset.fps
            : (asset.duration * state.fps) / asset.fps,
          actionId: actionId,
          effectId: asset.name || "",
          fps: asset.fps,
          titleId: asset.titleId,
          audioMix: asset.audioMix,
          audioMapping: asset.audioChannels && asset.audioChannels.map((x, i) => ({ ...x, inputIndex: i })),
        },
        effectId: asset.name || "",
        id: actionId,
        start: lastSequenceEnd,
        end: actionEnd,
      };

      let newTimelineActions: CusTomTimelineRow[] = state.timelineActions.map((x, i) => {
        if (i === 0) {
          return { ...x, actions: [newAction, ...x.actions] };
        } else return x;
      });
      state.timelineActions = newTimelineActions;
      const orderedActions = calculateOrderedActions(newTimelineActions, state.fps);
      state.orderedActionsList = orderedActions;
      if (orderedActions.length > 0)
        state.sequenceDuration = Math.round(orderedActions[orderedActions.length - 1].end * state.fps);
      else state.sequenceDuration = 0;

      if (state.linkedToPreviewActionId !== "") {
        newTimelineActions.forEach((x) => {
          x.actions.forEach((a) => {
            if (a.id === state.linkedToPreviewActionId) {
              state.inFrame = a.data.offSet;
              state.outFrame = (a.end - a.start) * state.fps + a.data.offSet;
            }
          });
        });
      }
    },
    AddToSequenceFromPreview: (state) => {
      if (!state.outFrame || !state.inFrame || !state.previewSrc || !state.selectedAssetFile || !state.selectedAsset)
        return;
      const lastSequenceEnd =
        state.timelineActions.length > 0
          ? state.timelineActions.reduce((acc, x) => {
              let result = acc;
              if (x.actions && x.actions.length > 0) {
                for (let i = 0; i < x.actions.length; i++) {
                  if (x.actions[i].end > result) result = x.actions[i].end;
                }
              }
              return result;
            }, 0)
          : 0;

      const actionId = uuidv4();

      let newAction: CustomTimelineAction = {
        data: {
          src: state.previewSrc,
          offSet: state.inFrame,
          name: state.selectedAssetFile.name || "",
          id: state.selectedAsset.id,
          type: state.selectedAsset.type,
          classification: state.selectedAsset.classification,
          duration: (state.selectedAsset.duration * state.fps) / state.selectedAsset.fps,
          actionId: actionId,
          effectId: state.selectedAssetFile.name || "",
          fps: state.selectedAsset.fps,
          titleId: state.selectedAsset.titleId,
          audioMix: state.selectedAsset.audioMix,
          audioMapping:
            state.selectedAsset.audioChannels &&
            state.selectedAsset.audioChannels.map((x, i) => ({ ...x, inputIndex: i })),
        },
        effectId: state.selectedAssetFile.name || "",
        id: actionId,
        start: lastSequenceEnd || 0,
        end: (lastSequenceEnd || 0) + (state.outFrame - state.inFrame) / state.fps,
      };

      let newTimelineActions: CusTomTimelineRow[] = state.timelineActions.map((x, i) => {
        if (i === 0) {
          return { ...x, actions: [newAction, ...x.actions] };
        } else return x;
      });

      state.timelineActions = newTimelineActions;
      const orderedActions = calculateOrderedActions(newTimelineActions, state.fps);
      state.orderedActionsList = orderedActions;
      if (orderedActions.length > 0)
        state.sequenceDuration = Math.round(orderedActions[orderedActions.length - 1].end * state.fps);
      else state.sequenceDuration = 0;
    },
    ToggleAutoScroll: (state) => {
      state.autoScroll = !state.autoScroll;
    },
    SetAudioState: (state, action) => {
      state.audioState = action.payload;
    },
    MoveAudioTrack: (state, action: PayloadAction<{ direction: "up" | "down"; index: number; actionId: string }>) => {
      const { direction, index, actionId } = action.payload;

      const linkedRow = state.timelineActions.find((r) => r.actions.find((x) => x.data.actionId === actionId));

      const linkedAction = linkedRow?.actions.find((x) => x.data.actionId === actionId);
      if (linkedAction?.data.audioMapping) {
        const swap1 = { ...linkedAction.data.audioMapping[index] };
        const swap2 = { ...linkedAction.data.audioMapping[direction === "up" ? index - 1 : index + 1] };
        linkedAction.data.audioMapping[index] = swap2;
        linkedAction.data.audioMapping[direction === "up" ? index - 1 : index + 1] = swap1;
      }

      const orderedActions = calculateOrderedActions(state.timelineActions, state.fps);
      state.orderedActionsList = orderedActions;
    },
    DeleteAudioTrack: (state, action: PayloadAction<{ index: number; actionId: string }>) => {
      const { index, actionId } = action.payload;

      const linkedRow = state.timelineActions.find((r) => r.actions.find((x) => x.data.actionId === actionId));

      const linkedAction = linkedRow?.actions.find((x) => x.data.actionId === actionId);
      if (linkedAction?.data.audioMapping) {
        linkedAction.data.audioMapping = linkedAction.data.audioMapping.filter((x, i) => i !== index);
      }
    },
    SetFetchingVideoFileData: (state, action: PayloadAction<boolean>) => {
      state.fetchingVideoFileData = action.payload;
    },
    SetHandleAudio: (state, action: PayloadAction<boolean>) => {
      state.handleAudio = action.payload;
      state.showSequence = false;
      state.orderedActionsList = calculateOrderedActions(state.timelineActions, state.fps);
    },
    SwitchAudioPlayMode: (state) => {
      state.audioPlayMode = state.audioPlayMode === "mono" ? "stereo" : "mono";
    },
  },
});

export const {
  InitState,
  SetLoadedSequence,
  SetSequenceName,
  SetFps,
  SetIsFetching,
  SetInAndOutFrame,
  SetLinkedToPreviewActionId,
  SetPreviewIsPlaying,
  SetPreviewTime,
  TogglePreviewIsPlaying,
  SetContext,
  ChangeActionRow,
  SetPreviewSrc,
  SetInFrame,
  SetOutFrame,
  SetPreviewDuration,
  SetSequenceTime,
  SetSequenceIsPlaying,
  SetSrc,
  SetShowSequence,
  ToggleLibrary,
  ToggleSequence,
  ToggleTimeline,
  SetCurrentFrame,
  SetTimelineActions,
  SetSelectedAsset,
  SetSelectedAssetFile,
  GenerateCalculatedSequence,
  DeleteAction,
  AddFirst,
  AddLast,
  ChangeEditMode,
  CutAction,
  SetIsLoading,
  ResetAfterError,
  CacheAfterError,
  SetZoomState,
  CutAtCursorPos,
  SetLoadedAssets,
  AddLoadedAssets,
  AddToSequence,
  AddToSequenceFromPreview,
  ToggleAutoScroll,
  SetAudioState,
  MoveAudioTrack,
  SetFetchingVideoFileData,
  SetHandleAudio,
  SwitchAudioPlayMode,
  DeleteAudioTrack,
} = timetoolSlice.actions;

export const GetIsLoading = (state: RootState): boolean => state.timetoolSlice.isLoading;
export const GetResetInAndOutFrame = (state: RootState): boolean => state.timetoolSlice.resetInAndOutFrame;
export const GetLoadedSequence = (state: RootState): DbAssetObjectInfos | null => state.timetoolSlice.loadedSequence;
export const GetSequenceName = (state: RootState): string => state.timetoolSlice.sequenceName;
export const GetSequenceIsPlaying = (state: RootState): boolean => state.timetoolSlice.sequenceIsPlaying;
export const GetSrc = (state: RootState): string => state.timetoolSlice.src;
export const GetShowSequence = (state: RootState): boolean => state.timetoolSlice.showSequence;
export const GetShowLibrary = (state: RootState): boolean => state.timetoolSlice.showLibrary;
export const GetShowTimeline = (state: RootState): boolean => state.timetoolSlice.showTimeline;
export const GetCurrentFrame = (state: RootState): number => state.timetoolSlice.currentFrame;
export const GetTimelineActions = (state: RootState): CusTomTimelineRow[] => state.timetoolSlice.timelineActions;
export const GetSelectedAsset = (state: RootState): any => state.timetoolSlice.selectedAsset;
export const GetSelectedAssetFile = (state: RootState): DbFileInfos | undefined =>
  state.timetoolSlice.selectedAssetFile;
export const GetFps = (state: RootState): number => state.timetoolSlice.fps;
export const GetSequenceTime = (state: RootState): number => state.timetoolSlice.sequenceTime;
export const GetPreviewTime = (state: RootState): number => state.timetoolSlice.previewTime;
export const GetOrderedActionsList = (state: RootState): CustomTimelineAction[] =>
  state.timetoolSlice.orderedActionsList;
export const GetSequenceDuration = (state: RootState): number => state.timetoolSlice.sequenceDuration;
export const GetInFrame = (state: RootState): number | null => state.timetoolSlice.inFrame;
export const GetOutFrame = (state: RootState): number | null => state.timetoolSlice.outFrame;
export const GetPreviewDuration = (state: RootState): number => state.timetoolSlice.previewDuration;
export const GetPreviewSrc = (state: RootState): string => state.timetoolSlice.previewSrc;
export const GetContext = (state: RootState): string => state.timetoolSlice.context;
export const GetClipInOut =
  (actionId: number) =>
  (state: RootState): (number | undefined)[] => {
    const foundAction = state.timetoolSlice.orderedActionsList.find((x) => x.id === actionId.toString());
    return [foundAction?.start, foundAction?.end];
  };
export const GetPreviewIsPlaying = (state: RootState): boolean => state.timetoolSlice.previewIsPlaying;
export const GetLinkedToPreviewActionId = (state: RootState): string => state.timetoolSlice.linkedToPreviewActionId;
export const GetIsFetching = (state: RootState): boolean => state.timetoolSlice.isFetching;
export const GetEditMode = (state: RootState): string => state.timetoolSlice.editMode;
export const GetPreviewFps = (state: RootState): number => state.timetoolSlice.previewFps;
export const GetPlayerError = (state: RootState): string | null => state.timetoolSlice.playerError;
export const GetZoomState = (state: RootState): { scale: number; width: number } => state.timetoolSlice.zoomState;
export const GetLoadedAssets = (state: RootState): any[] => state.timetoolSlice.loadedAssets;
export const GetAutoScroll = (state: RootState): boolean => state.timetoolSlice.autoScroll;
export const GetAudioMix =
  (src: string | null) =>
  (state: RootState): any =>
    state.timetoolSlice.orderedActionsList?.find((x) => x.data.src === src)?.data.audioMix ?? null;
export const GetAudioState = (state: RootState): any => state.timetoolSlice.audioState;
export const GetFetchingVideoFileData = (state: RootState): boolean => state.timetoolSlice.fetchingVideoFileData;
export const GetHandleAudio = (state: RootState): boolean => state.timetoolSlice.handleAudio;
export const GetAudioPlayMode = (state: RootState): "mono" | "stereo" => state.timetoolSlice.audioPlayMode;

export default timetoolSlice.reducer;

export const calculateNextTimelineState = (
  oldTimelineData: CusTomTimelineRow[],
  newTimelineData: CusTomTimelineRow[],
  fps: number,
  fpsChange?: boolean
): CusTomTimelineRow[] => {
  const newRowActionsList: any[] = [];
  if (oldTimelineData === undefined || newTimelineData === undefined) return oldTimelineData;

  for (let i = 0; i < newTimelineData.length; i++) {
    const newActionList: CusTomTimelineRow = { ...oldTimelineData[i], actions: [] };
    const orderedActions: CustomTimelineAction[] = newTimelineData[i].actions.sort((a, b) => a.end - b.end);

    for (let n = 0; n < newTimelineData[i].actions.length; n++) {
      const existingAction = oldTimelineData
        .find((x) => x.id === newTimelineData[i].id)
        ?.actions?.find((x) => x.id === newTimelineData[i].actions[n].id);

      if (!existingAction) return oldTimelineData;

      const startChange =
        Math.round(existingAction.start * 100) !== Math.round(newTimelineData[i].actions[n].start * 100);
      const endChange = Math.round(existingAction.end * 100) !== Math.round(newTimelineData[i].actions[n].end * 100);

      if (startChange || endChange || fpsChange) {
        let orderedActions;
        if (endChange && !startChange) {
          orderedActions = newTimelineData[i].actions.sort((a, b) => a.start - b.start);
        } else {
          orderedActions = newTimelineData[i].actions.sort((a, b) => a.end - b.end);
        }
        const orderedIndex = orderedActions.findIndex((x) => x.id === newTimelineData[i].actions[n].id);
        let newActionStart;
        let newActionEnd;
        let newActionOffSet;

        if (
          (orderedActions[orderedIndex].start > orderedActions[orderedIndex - 1]?.start &&
            orderedActions[orderedIndex].end < orderedActions[orderedIndex - 1]?.end) ||
          (orderedActions[orderedIndex].start > orderedActions[orderedIndex + 1]?.start &&
            orderedActions[orderedIndex].end < orderedActions[orderedIndex + 1]?.end) ||
          (orderedActions[orderedIndex].start < orderedActions[orderedIndex - 1]?.start &&
            orderedActions[orderedIndex].end > orderedActions[orderedIndex - 1]?.end) ||
          (orderedActions[orderedIndex].start < orderedActions[orderedIndex + 1]?.start &&
            orderedActions[orderedIndex].end > orderedActions[orderedIndex + 1]?.end)
        ) {
          return oldTimelineData.map((x) => ({ ...x, actions: x.actions.length > 0 ? x.actions.map((y) => y) : [] }));
        }

        newActionStart = startChange
          ? Math.max(
              orderedActions[orderedIndex - 1]?.end || 0,
              orderedActions[orderedIndex].start,
              orderedActions[orderedIndex].end - orderedActions[orderedIndex].data.duration
            )
          : orderedActions[orderedIndex].start;

        newActionOffSet =
          Math.round((existingAction.end - existingAction.start) * 100) !==
            Math.round((orderedActions[orderedIndex].end - newActionStart) * 100) && !endChange
            ? Math.max(
                orderedActions[orderedIndex].data.offSet +
                  Math.round((Math.round(newActionStart * 100) - Math.round(existingAction.start * 100)) * fps * 0.01),
                0
              )
            : orderedActions[orderedIndex].data.offSet;
        newActionEnd = endChange
          ? Math.min(
              orderedActions[orderedIndex].end,
              orderedActions[orderedIndex].data.duration - newActionOffSet / fps + newActionStart,
              orderedActions[orderedIndex + 1]?.start || 99999
            )
          : orderedActions[orderedIndex].end;

        newActionList.actions.push({
          ...orderedActions[n],
          start: Math.round(newActionStart * 100) / 100,
          end: Math.round(newActionEnd * 100) / 100,
          data: {
            ...orderedActions[n].data,
            offSet: newActionOffSet > 0 ? Math.round(newActionOffSet) : 0,
          },
        });
      } else
        newActionList.actions.push({
          ...orderedActions[n],
          start: Math.round(orderedActions[n].start * 100) / 100,
          end: Math.round(orderedActions[n].end * 100) / 100,
        });
    }
    newRowActionsList.push(newActionList);
  }

  return newRowActionsList;
};

const calculateOrderedActions = (timelineActions: any, fps: number): CustomTimelineAction[] => {
  if (!timelineActions || !(timelineActions?.length > 0)) return timelineActions;

  let mainFrame: any[] = [
    ...timelineActions[0].actions
      .map((x) => ({ ...x, start: Math.round(x.start * 100) / 100, end: Math.round(x.end * 100) / 100 }))
      .sort((a, b) => a.start - b.start),
  ];

  for (let i = 1; i < timelineActions.length; i++) {
    let orderedActions = [...timelineActions[i].actions.map((x) => x).sort((a, b) => a.start - b.start)];

    for (let a = 0; a < orderedActions.length; a++) {
      const cacheFrame = [...mainFrame];
      const insertIndexStart = cacheFrame.findLastIndex((x) => orderedActions[a].start >= x.start);
      const insertIndexEnd =
        cacheFrame.findIndex((x) => orderedActions[a].end <= x.end) >= 0
          ? cacheFrame.findIndex((x) => orderedActions[a].end <= x.end)
          : cacheFrame.length;

      for (let d = insertIndexStart; d < insertIndexEnd; d++) {
        const retainStart =
          d === -1 ? orderedActions[a].start : Math.max(orderedActions[a].start, cacheFrame[d]?.end || 0);
        const retainEnd = Math.min(orderedActions[a].end, cacheFrame[d + 1]?.start || 999999);
        const newOffSet = orderedActions[a].data.offSet + (retainStart - orderedActions[a].start) * fps;
        const actionId = uuidv4();
        const insertAction = {
          ...orderedActions[a],
          id: actionId,
          start: Math.round(retainStart * 100) / 100,
          end: Math.round(retainEnd * 100) / 100,
          data: { ...orderedActions[a].data, offSet: Math.round(newOffSet), actionId: actionId },
        };
        mainFrame.splice(d, 0, insertAction);
      }
    }
    mainFrame = mainFrame.sort((a, b) => a.start - b.start);
  }

  const sortedActionsWithCuts: any[] = [];

  for (let i = 0; i < mainFrame.length; i++) {
    if (i < mainFrame.length - 1) {
      if (i === 0 && mainFrame[i].start > 0) {
        sortedActionsWithCuts.push({ data: { src: "", offSet: 0 }, start: 0, end: mainFrame[i].start });
      }
      if (mainFrame[i].end < mainFrame[i + 1].start) {
        sortedActionsWithCuts.push({
          data: { src: "", offSet: 0 },
          start: mainFrame[i].end,
          end: mainFrame[i + 1].start,
        });
      }
    } else if (i === 0 && mainFrame[i].start > 0) {
      sortedActionsWithCuts.push({
        data: { src: "", offSet: 0 },
        start: 0,
        end: mainFrame[i].start,
      });
    }
    sortedActionsWithCuts.push(mainFrame[i]);
  }
  return sortedActionsWithCuts.sort((a, b) => a.start - b.start);
};

export const generateMetadata = (timelineActions: any[], currentFps: number) => {
  const result: any[] = [];
  for (let i = 0; i < timelineActions.length; i++) {
    for (let n = 0; n < timelineActions[i].actions.length; n++) {
      const frameOut = Math.round(
        timelineActions[i].actions[n].data.offSet +
          Math.round((timelineActions[i].actions[n].end - timelineActions[i].actions[n].start) * currentFps)
      );
      const frameIn = !timelineActions[i].actions[n].data.offSet
        ? null
        : Math.round(timelineActions[i].actions[n].data.offSet);
      const originalEnd = Math.round(
        timelineActions[i].actions[n].data.fps * timelineActions[i].actions[n].data.duration
      );

      let segment = {
        objectId: timelineActions[i].actions[n].data.id,
        frameRate: currentFps,
        frameIn: frameIn,
        frameOut: frameOut === originalEnd ? null : frameOut,
        frameStart: Math.round(timelineActions[i].actions[n].start * currentFps),
        track: i,
        titleId: timelineActions[i].actions[n].data.titleId,
        audioChannels: timelineActions[i].actions[n].data.audioMapping.map((x, y) => ({
          inputIndex: x.trackNumber - 1,
          targetIndex: y,
        })),
      };

      if (segment.frameOut && segment.frameIn && segment.frameOut <= segment.frameIn) continue;
      result.push(segment);
    }
  }
  return result;
};
